<script setup lang="ts">
import { IconChatActive, IconCheck, IconClose, IconSearch } from '@/components/design/icons';
import { canFocusSuggestion } from '@/office/playbooks/suggestionPreview';
import type { WordSuggestion } from '@/types/playbookWordAddin';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';

/**
 * The per-result controls: find it in the document, and — for a suggestion
 * with a redline — comment, apply or decline it. Square quaternary icon
 * buttons, as on every row control in the web app.
 */
const props = defineProps<{
  suggestion: WordSuggestion;
  /** Extraction cards show only "show in document" — they have no redline to apply. */
  actionable: boolean;
  focusingId: string | null;
  commentingId: string | null;
  applyingId: string | null;
}>();

defineEmits<{
  focus: [suggestion: WordSuggestion];
  comment: [suggestion: WordSuggestion];
  apply: [suggestion: WordSuggestion];
  decline: [suggestion: WordSuggestion];
}>();

/** Commenting anchors the same way applying does, so it needs both to be possible. */
function canComment(): boolean {
  return (
    props.suggestion.status !== 'declined' &&
    canFocusSuggestion(props.suggestion) &&
    props.actionable
  );
}
</script>

<template>
  <n-flex
    align="center"
    justify="end"
    :size="2"
    :wrap="false"
    :style="{ marginLeft: 'auto', flex: 'none' }"
    @click.stop
  >
    <gaia-tooltip v-if="canFocusSuggestion(suggestion)">
      <template #trigger>
        <gaia-icon-button
          :dimension="28"
          quaternary
          :loading="focusingId === suggestion.id"
          aria-label="Show affected text in document"
          @click.stop="$emit('focus', suggestion)"
        >
          <n-icon :size="15" :component="IconSearch" />
        </gaia-icon-button>
      </template>
      Show in document
    </gaia-tooltip>

    <template v-if="actionable">
      <gaia-tooltip>
        <template #trigger>
          <gaia-icon-button
            :dimension="28"
            quaternary
            :disabled="!canComment()"
            :loading="commentingId === suggestion.id"
            aria-label="Add suggestion as Word comment"
            @click.stop="$emit('comment', suggestion)"
          >
            <n-icon :size="15" :component="IconChatActive" />
          </gaia-icon-button>
        </template>
        Add as comment
      </gaia-tooltip>

      <gaia-tooltip>
        <template #trigger>
          <gaia-icon-button
            :dimension="28"
            quaternary
            :disabled="suggestion.status === 'declined'"
            :loading="applyingId === suggestion.id"
            :aria-label="
              suggestion.status === 'accepted' ? 'Re-apply suggestion' : 'Apply suggestion'
            "
            @click.stop="$emit('apply', suggestion)"
          >
            <n-icon :size="15" :component="IconCheck" />
          </gaia-icon-button>
        </template>
        {{ suggestion.status === 'accepted' ? 'Re-apply suggestion' : 'Apply suggestion' }}
      </gaia-tooltip>

      <gaia-tooltip>
        <template #trigger>
          <gaia-icon-button
            :dimension="28"
            quaternary
            :disabled="suggestion.status !== 'pending'"
            aria-label="Decline suggestion"
            @click.stop="$emit('decline', suggestion)"
          >
            <n-icon :size="15" :component="IconClose" />
          </gaia-icon-button>
        </template>
        Decline suggestion
      </gaia-tooltip>
    </template>
  </n-flex>
</template>
